/**
 * DailyTimeNotificationModal.js
 * 당일 투입시간 알림 모달 컴포넌트
 * 우측 하단에 고정되어 표시됨
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Slide,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { X, Clock, TrendingUp } from 'react-feather';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import apiConfig from '../config/api';

const DailyTimeNotificationModal = ({ open, onClose, onNavigate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchDailySummary();
    }
  }, [open]);

  const fetchDailySummary = async () => {
    setLoading(true);
    try {
      // 인증 토큰 확인
      const token = localStorage.getItem('token');
      if (!token) {
        // 인증 토큰이 없으면 로그인 유도 상태로 설정
        setSummary({
          date: new Date().toISOString().split('T')[0],
          total_minutes: 0,
          total_hours: 0,
          count: 0,
          has_data: false,
          current_streak: 0,
          isAuthenticated: false,
        });
        setLoading(false);
        return;
      }

      const today = new Date().toISOString().split('T')[0];
      const response = await axios.get(`${apiConfig.TIMELOGS.DAILY_SUMMARY}?target_date=${today}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      // API 응답을 그대로 사용 (current_streak 기본값 설정)
      setSummary({
        ...response.data,
        current_streak: response.data.current_streak || 0,
        isAuthenticated: true,
      });
    } catch (error) {
      console.error('당일 투입시간 조회 실패:', error);
      
      // 401 에러면 인증 토큰이 만료된 것으로 간주
      const isUnauthorized = error.response?.status === 401;
      
      setSummary({
        date: new Date().toISOString().split('T')[0],
        total_minutes: 0,
        total_hours: 0,
        count: 0,
        has_data: false,
        current_streak: 0,
        isAuthenticated: !isUnauthorized, // 401이면 인증되지 않은 것으로 표시
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => {
    // 인증 토큰이 없거나 인증되지 않은 경우 로그인 페이지로 이동
    const token = localStorage.getItem('token');
    const isAuthenticated = summary?.isAuthenticated !== false && !!token;
    
    let path;
    if (isAuthenticated) {
      // 모바일/PC 경로 자동 감지
      const isMobileDevice = window.innerWidth <= 768;
      path = isMobileDevice ? '/m/log' : '/p/job-time-input';
    } else {
      // 로그인 페이지로 이동
      path = '/login';
    }
    
    if (onNavigate) {
      onNavigate(path);
    } else {
      navigate(path);
    }
    onClose();
  };

  if (!open) return null;

  return (
    <Slide direction="left" in={open} mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: isMobile ? 16 : 24,
          right: isMobile ? 16 : 24,
          width: isMobile ? 'calc(100% - 32px)' : 380,
          maxWidth: 380,
          zIndex: 9999,
          borderRadius: 3,
          overflow: 'hidden',
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(140deg, rgba(32, 34, 42, 0.98) 0%, rgba(18, 19, 26, 0.95) 100%)'
            : '#FFFFFF',
          backdropFilter: theme.palette.mode === 'dark' ? 'blur(20px)' : 'none',
          WebkitBackdropFilter: theme.palette.mode === 'dark' ? 'blur(20px)' : 'none',
          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(158, 164, 189, 0.3)' : 'rgba(221, 225, 230, 0.9)'}`,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 28px 48px -18px rgba(0, 0, 0, 0.85), inset 0 1px 0 rgba(255, 255, 255, 0.08)'
            : '0 12px 28px -16px rgba(15, 29, 68, 0.15)',
          cursor: 'pointer',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 32px 58px -16px rgba(0, 0, 0, 0.9), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
              : '0 18px 34px -18px rgba(15, 29, 68, 0.2)',
          },
        }}
        onClick={handleClick}
      >
        <Box sx={{ p: 2.5 }}>
          {/* 헤더 */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(123, 104, 238, 0.2) 0%, rgba(181, 165, 255, 0.15) 100%)'
                    : 'linear-gradient(135deg, rgba(165, 148, 249, 0.15) 0%, rgba(123, 104, 238, 0.1) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Clock
                  size={20}
                  color={theme.palette.mode === 'dark' ? '#B5A5FF' : '#7B68EE'}
                />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                오늘의 투입시간
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <X size={18} />
            </IconButton>
          </Box>

          {/* 내용 */}
          {loading ? (
            <Box sx={{ py: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                조회 중...
              </Typography>
            </Box>
          ) : summary ? (
            <Box>
              {summary.isAuthenticated === false ? (
                <>
                  <Box sx={{ textAlign: 'center', py: 1 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: '16px',
                        fontWeight: 500,
                        color: theme.palette.text.primary,
                        mb: 1,
                      }}
                    >
                      OnTimeLog 시간을 입력하셨나요?
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '14px',
                        color: theme.palette.text.secondary,
                        mb: 2,
                      }}
                    >
                      여기를 클릭하시면 바로 접속이 가능합니다
                    </Typography>
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{
                        py: 1.2,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        background: theme.palette.mode === 'dark'
                          ? 'linear-gradient(135deg, rgba(123, 104, 238, 0.9) 0%, rgba(181, 165, 255, 0.8) 100%)'
                          : 'linear-gradient(135deg, rgba(123, 104, 238, 0.95) 0%, rgba(165, 148, 249, 0.9) 100%)',
                        '&:hover': {
                          background: theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg, rgba(123, 104, 238, 1) 0%, rgba(181, 165, 255, 0.95) 100%)'
                            : 'linear-gradient(135deg, rgba(123, 104, 238, 1) 0%, rgba(165, 148, 249, 1) 100%)',
                        },
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClick();
                      }}
                    >
                      접속하기
                    </Button>
                  </Box>
                </>
              ) : summary.has_data ? (
                <>
                  {/* 연속 입력 달성 메시지 */}
                  {summary.current_streak > 0 && (
                    <Box
                      sx={{
                        mb: 1.5,
                        p: 1.5,
                        borderRadius: 2,
                        background: theme.palette.mode === 'dark'
                          ? 'linear-gradient(135deg, rgba(255, 122, 184, 0.15) 0%, rgba(123, 104, 238, 0.1) 100%)'
                          : 'linear-gradient(135deg, rgba(255, 122, 184, 0.1) 0%, rgba(123, 104, 238, 0.08) 100%)',
                        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 122, 184, 0.3)' : 'rgba(123, 104, 238, 0.2)'}`,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '14px',
                          fontWeight: 600,
                          color: theme.palette.mode === 'dark' ? '#FF7AB8' : '#7B68EE',
                          textAlign: 'center',
                        }}
                      >
                        {summary.current_streak >= 30
                          ? `🔥 ${summary.current_streak}일 연속 입력 달성! 정말 대단합니다!`
                          : summary.current_streak >= 14
                          ? `🔥 ${summary.current_streak}일 연속 입력 달성! 훌륭합니다!`
                          : summary.current_streak >= 7
                          ? `🔥 ${summary.current_streak}일 연속 입력 달성! 잘하고 있어요!`
                          : `🔥 ${summary.current_streak}일 연속 입력 달성!`}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.5 }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontSize: '32px',
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                        lineHeight: 1,
                      }}
                    >
                      {summary.total_hours}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '14px',
                        color: theme.palette.text.secondary,
                        ml: 0.5,
                      }}
                    >
                      시간
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <TrendingUp size={16} color={theme.palette.success.main} />
                    <Typography variant="body2" color="text.secondary">
                      {summary.count}건 입력됨
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{
                      mt: 1,
                      py: 1.2,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, rgba(123, 104, 238, 0.9) 0%, rgba(181, 165, 255, 0.8) 100%)'
                        : 'linear-gradient(135deg, rgba(123, 104, 238, 0.95) 0%, rgba(165, 148, 249, 0.9) 100%)',
                      '&:hover': {
                        background: theme.palette.mode === 'dark'
                          ? 'linear-gradient(135deg, rgba(123, 104, 238, 1) 0%, rgba(181, 165, 255, 0.95) 100%)'
                          : 'linear-gradient(135deg, rgba(123, 104, 238, 1) 0%, rgba(165, 148, 249, 1) 100%)',
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClick();
                    }}
                  >
                    투입시간 입력하기
                  </Button>
                </>
              ) : (
                <>
                  <Box sx={{ textAlign: 'center', py: 1 }}>
                    {/* 연속 입력 달성 메시지 (입력이 없어도 표시) */}
                    {summary.current_streak > 0 && (
                      <Box
                        sx={{
                          mb: 2,
                          p: 1.5,
                          borderRadius: 2,
                          background: theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg, rgba(255, 122, 184, 0.15) 0%, rgba(123, 104, 238, 0.1) 100%)'
                            : 'linear-gradient(135deg, rgba(255, 122, 184, 0.1) 0%, rgba(123, 104, 238, 0.08) 100%)',
                          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 122, 184, 0.3)' : 'rgba(123, 104, 238, 0.2)'}`,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '14px',
                            fontWeight: 600,
                            color: theme.palette.mode === 'dark' ? '#FF7AB8' : '#7B68EE',
                            textAlign: 'center',
                          }}
                        >
                          {summary.current_streak >= 30
                            ? `🔥 ${summary.current_streak}일 연속 입력 달성! 정말 대단합니다!`
                            : summary.current_streak >= 14
                            ? `🔥 ${summary.current_streak}일 연속 입력 달성! 훌륭합니다!`
                            : summary.current_streak >= 7
                            ? `🔥 ${summary.current_streak}일 연속 입력 달성! 잘하고 있어요!`
                            : `🔥 ${summary.current_streak}일 연속 입력 달성!`}
                        </Typography>
                      </Box>
                    )}
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: '16px',
                        fontWeight: 500,
                        color: theme.palette.text.primary,
                        mb: 1,
                      }}
                    >
                      아직 입력된 시간이 없습니다
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '14px',
                        color: theme.palette.text.secondary,
                        mb: 2,
                      }}
                    >
                      오늘의 업무 투입시간을 입력해주세요
                    </Typography>
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{
                        py: 1.2,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        background: theme.palette.mode === 'dark'
                          ? 'linear-gradient(135deg, rgba(123, 104, 238, 0.9) 0%, rgba(181, 165, 255, 0.8) 100%)'
                          : 'linear-gradient(135deg, rgba(123, 104, 238, 0.95) 0%, rgba(165, 148, 249, 0.9) 100%)',
                        '&:hover': {
                          background: theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg, rgba(123, 104, 238, 1) 0%, rgba(181, 165, 255, 0.95) 100%)'
                            : 'linear-gradient(135deg, rgba(123, 104, 238, 1) 0%, rgba(165, 148, 249, 1) 100%)',
                        },
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClick();
                      }}
                    >
                      투입시간 입력하기
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          ) : (
            <Box sx={{ py: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                데이터를 불러올 수 없습니다
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    </Slide>
  );
};

export default DailyTimeNotificationModal;

